import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Modal from '@material-ui/core/Modal';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Grid from '@material-ui/core/Grid';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import MoreVertIcon from '@material-ui/icons/MoreVert';

import AdminLayout from '../../../layouts/AdminLayout';
import Announcement from '../../../models/Announcement';
import Course from '../../../models/Course';
import Utils from '../../../libs/utils';

interface AllCourses {
  id: string;
  name: string;
}

const AnnouncementView = () => {
  const [openModal, setOpenModal] = useState(false);
  const [courses, setCourses] = useState<AllCourses[]>([]);
  const [courseID, setCourseID] = useState('');
  const [message, setMessage] = useState('');
  const [announcementID, setAnnouncementID] = useState<string | undefined>('');
  const [expiration, setExpiration] = useState('');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [editing, setEditing] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const useStyles = makeStyles((theme) => ({
    table: {
      minWidth: 650,
    },
    spaceTop: {
      margin: '25px 0px 25px 25px',
      float: 'right',
    },
    bold: {
      fontWeight: 'bold',
    },
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paper: {
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
      width: '600px',
    },
    root: {
      '& .MuiTextField-root': {
        margin: theme.spacing(1),
        width: '25ch',
      },
    },
  }));

  const classes = useStyles();

  useEffect(() => {
    getCourses();
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    let arr = await Announcement.getAnnouncments();
    setAnnouncements(arr);
  };

  const getCourses = async () => {
    const course = await Course.getCourses();
    setCourses([{id: '*', name: 'All'}, ...course]);
  };

  const toggleModal = () => {
    setCourseID('');
    setExpiration('');
    setMessage('');
    setAnnouncementID('');
    setOpenModal(!openModal);
    setSubmitted(false);
    if (editing) setEditing(false);
  };

  const isActive = (date: string) => {
    if (new Date().getTimezoneOffset() === -480) {
      return new Date(date) > Utils.offsetDateToUS(new Date().toString());
    }
    return new Date(date) > new Date(); // For US TA's
  };

  const edit = (announcement: Announcement, popupState: any) => {
    setEditing(true);
    toggleModal();
    setAnnouncementID(announcement.announcementID);
    setCourseID(announcement.courseID);
    setExpiration(announcement.expiration);
    setMessage(announcement.message);
    popupState.close();
  };

  const deleteAnnouncement = async (
    announcement: Announcement,
    popupState: any
  ) => {
    const instance = new Announcement(announcement);
    const res = await instance.deleteAnnouncement();
    if (res) {
      loadAnnouncements();
    }
    popupState.close();
  };

  const submitAnnouncement = async (event: React.FormEvent) => {
    event.preventDefault();

    setSubmitted(true);

    if (!editing && courseID.trim() !== '' && expiration.trim() !== '' && message.trim() !== '' && courses.some(c => c.id === courseID)) {
      const announcement = new Announcement({
        courseID,
        expiration,
        message,
      } as Announcement);
      const res = await announcement.createAnnouncement();
      if (res) {
        loadAnnouncements();
        toggleModal();
      }
    } else if(editing && courseID.trim() !== '' && expiration.trim() !== '' && message.trim() !== '' && courses.some(c => c.id === courseID)) {
      const announcement = new Announcement({
        announcementID,
        courseID,
        expiration,
        message,
      } as Announcement);
      const res = await announcement.updateAnnouncement();
      if (res) {
        loadAnnouncements();
        toggleModal();
      }
    }
  };

  return (
    <AdminLayout>
      <Button
        variant="contained"
        color="primary"
        className={classes.spaceTop}
        onClick={toggleModal}
      >
        Create Announcement
      </Button>
      <TableContainer component={Paper}>
        <Table className={classes.table} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell className={classes.bold}>Course ID</TableCell>
              <TableCell className={classes.bold} align="right">
                Message
              </TableCell>
              <TableCell className={classes.bold} align="right">
                Status
              </TableCell>
              <TableCell className={classes.bold} align="right">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {announcements.map((row, i) => (
              <TableRow key={row.courseID + i}>
                <TableCell component="th" scope="row">
                  {row.courseID !== '*'?row.courseID:'All'}
                </TableCell>
                <TableCell align="right">{row.message}</TableCell>
                <TableCell align="right">
                  {isActive(row.expiration) ? (
                    <span style={{ color: 'green' }}>Active</span>
                  ) : (
                    <span style={{ color: 'red' }}>Expired</span>
                  )}
                </TableCell>
                <TableCell align="right">
                  <PopupState variant="popover" popupId="demo-popup-menu">
                    {(popupState) => (
                      <React.Fragment>
                        <IconButton
                          color="primary"
                          {...bindTrigger(popupState)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu {...bindMenu(popupState)} keepMounted>
                          <MenuItem onClick={() => edit(row, popupState)}>
                            Edit
                          </MenuItem>
                          <MenuItem
                            onClick={() => deleteAnnouncement(row, popupState)}
                          >
                            Delete
                          </MenuItem>
                        </Menu>
                      </React.Fragment>
                    )}
                  </PopupState>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal
        open={openModal}
        onClose={toggleModal}
        className={classes.modal}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <div className={classes.paper}>
          <h4 id="simple-modal-title">Create Announcement</h4>
          <p id="simple-modal-description">
            <form
              className={classes.root}
              noValidate
              autoComplete="off"
              onSubmit={submitAnnouncement}
            >
              <Grid container>
                <Autocomplete
                  id="free-solo-demo"
                  style={{ flex: 1, padding: 7 }}
                  freeSolo
                  value={courses.find((e) => e.id === courseID)?.name}
                  options={courses.map((option) => option.name)}
                  onInputChange={(e, value) => {
                    const val = courses.find((e) => e.name === value);
                    setCourseID(val ? val.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      required
                      {...params}
                      label="Select Course"
                      style={{ width: '100%', margin: 0 }}
                      error={submitted && courseID === ''}
                      helperText={submitted && courseID ===''?'Not a valid course id':''}
                    />
                  )}
                />
                <TextField
                  required
                  style={{ width: '100%' }}
                  id="datetime-local"
                  label="Expiration (US Time)"
                  type="datetime-local"
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={submitted && expiration.trim() ===''}
                  helperText={submitted && expiration.trim() ===''?'Date should not be empty':''}
                />
                <TextField
                  required
                  aria-label="minimum height"
                  rows={4}
                  placeholder="Message"
                  value={message}
                  multiline
                  
                  onChange={(e) => setMessage(e.target.value)}
                  style={{
                    width: '100%',
                  }}
                  error={submitted && message.trim() ===''}
                  helperText={submitted && message.trim() ===''?'Message should not be empty':''}
                />
                <Grid
                  container
                  direction="row"
                  justify="flex-end"
                  alignItems="center"
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    style={{ margin: 7 }}
                  >
                    {editing ? 'Edit' : 'Create'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </p>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default AnnouncementView;
